import React from 'react'
import { Link } from '@inertiajs/react'
import { ResponsiveContainer, AreaChart, Area, YAxis } from 'recharts'
import { ArrowUpRight, ArrowDownRight, CheckCircle2, AlertCircle, Sparkles, MoveRight } from 'lucide-react'
import { formatMoney } from '@/lib/format'

interface SparklinePoint {
  date: string
  occ: number
}

interface PropertyCardData {
  id: number
  name: string
  /** The canonical path of this hotel — the card is a link to it. Backend-served. */
  basePath: string
  shortname: string
  currency: string
  totalRooms: number
  bookedTonight: number
  occupancyPct: number
  adr: number
  revpar: number
  stlyOccupancyPct: number
  stlyAdr: number
  stlyRevpar: number
  stlyHasData: boolean
  sparkline: SparklinePoint[]
  /**
   * Confirmed bookings CREATED in the trailing 7 days, across all stay dates. Not "pickup" —
   * pickup is the net change in room-nights on a fixed forward window and can be negative
   * (docs/revenue-vocabulary.md §6). This only ever counts up.
   */
  newBookings7d: number
  syncStatus: {
    lastStructureSync: string | null
    lastComputation: string | null
    lastPush: string | null
    lastPushCount: number | null
    errorCode: string | null
    errorMessage: string | null
    errorAt: string | null
  }
}

function hoursAgo(iso: string | null): number | null {
  if (!iso) return null
  return (new Date().getTime() - new Date(iso).getTime()) / 3_600_000
}

function shortTimeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const h = hoursAgo(iso)
  if (h === null) return 'never'
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`
  if (h < 24) return `${Math.round(h)}h`
  return `${Math.round(h / 24)}d`
}

function deltaPct(current: number, prior: number): { trend: 'up' | 'down' | 'flat'; label: string } {
  if (prior <= 0) return { trend: 'flat', label: '—' }
  const diff = current - prior
  if (Math.abs(diff) < 0.01) return { trend: 'flat', label: '0.0%' }
  const pct = ((diff / prior) * 100).toFixed(1)
  return { trend: diff > 0 ? 'up' : 'down', label: `${diff > 0 ? '+' : ''}${pct}%` }
}

function DeltaChip({ current, prior, hasData }: { current: number; prior: number; hasData: boolean }) {
  if (!hasData) {
    return <span className="text-[10px] text-muted-foreground/60">no last year</span>
  }
  const { trend, label } = deltaPct(current, prior)
  if (trend === 'flat') return <span className="text-[10px] text-muted-foreground tabular-nums">{label}</span>
  const Icon = trend === 'up' ? ArrowUpRight : ArrowDownRight
  const cls = trend === 'up' ? 'text-emerald-600' : 'text-rose-600'
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums ${cls}`}>
      <Icon className="h-2.5 w-2.5" />
      {label}
    </span>
  )
}

interface StatusPipProps {
  ok: boolean
  label: string
  age: string
  warn?: boolean
}

function StatusPip({ ok, label, age, warn }: StatusPipProps) {
  const Icon = ok ? CheckCircle2 : AlertCircle
  const cls = ok ? 'text-emerald-500' : warn ? 'text-amber-500' : 'text-rose-500'
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`h-3 w-3 ${cls}`} />
      <span className="text-[11px] text-muted-foreground">
        <span className="font-medium text-foreground">{label}</span>
        <span className="ml-1 tabular-nums">{age}</span>
      </span>
    </div>
  )
}

interface PropertyCardProps {
  card: PropertyCardData
}

export default function PropertyCard({ card }: PropertyCardProps) {
  const computeAgeH = hoursAgo(card.syncStatus.lastComputation)
  const computeOk = computeAgeH !== null && computeAgeH < 4
  const pushAgeH = hoursAgo(card.syncStatus.lastPush)
  const pushOk = pushAgeH !== null && pushAgeH < 24

  // Find sparkline peak for context
  const peakOcc = Math.max(...card.sparkline.map((p) => p.occ))

  return (
    <Link
      href={card.basePath}
      className="group relative block overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
    >
      {/* Top accent stripe */}
      <div className="h-[3px] bg-gradient-to-r from-primary via-primary to-emerald-400" />

      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              {card.shortname}
            </div>
            <h3 className="mt-1 truncate text-xl font-semibold tracking-tight text-foreground">
              {card.name}
            </h3>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Tonight
            </div>
            <div className="mt-1 text-sm font-semibold tabular-nums text-foreground">
              {card.bookedTonight} / {card.totalRooms}
            </div>
          </div>
        </div>

        {/* 3-up KPIs */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Occupancy
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-foreground">
              {card.occupancyPct.toFixed(1)}<span className="text-base font-medium text-muted-foreground">%</span>
            </div>
            <div className="mt-1">
              <DeltaChip
                current={card.occupancyPct}
                prior={card.stlyOccupancyPct}
                hasData={card.stlyHasData}
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              ADR
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-foreground">
              {formatMoney(card.adr, card.currency)}
            </div>
            <div className="mt-1">
              <DeltaChip current={card.adr} prior={card.stlyAdr} hasData={card.stlyHasData} />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              RevPAR
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-foreground">
              {formatMoney(card.revpar, card.currency)}
            </div>
            <div className="mt-1">
              <DeltaChip current={card.revpar} prior={card.stlyRevpar} hasData={card.stlyHasData} />
            </div>
          </div>
        </div>

        {/* Sparkline */}
        <div className="mt-6">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Next 14 days · occupancy
            </span>
            <span className="text-[10px] tabular-nums text-muted-foreground">
              peak {peakOcc.toFixed(0)}%
            </span>
          </div>
          <div className="h-14 -mx-1">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 56 }}>
              <AreaChart data={card.sparkline} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id={`spark-${card.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0d9488" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <YAxis domain={[0, 100]} hide />
                <Area
                  type="monotone"
                  dataKey="occ"
                  stroke="#0d9488"
                  strokeWidth={1.5}
                  fill={`url(#spark-${card.id})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-5 flex items-center justify-between gap-3 border-t border-border/40 pt-4">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-primary" />
            <span
              className="text-[11px] text-muted-foreground"
              title="Confirmed bookings created in the last 7 days, across all stay dates."
            >
              <span className="font-semibold tabular-nums text-foreground">{card.newBookings7d}</span>{' '}
              {card.newBookings7d === 1 ? 'new booking' : 'new bookings'} · 7d
            </span>
          </div>
          <div className="flex items-center gap-3">
            <StatusPip ok={computeOk} label="rates" age={shortTimeAgo(card.syncStatus.lastComputation)} warn={computeAgeH !== null && computeAgeH >= 4 && computeAgeH < 24} />
            <StatusPip ok={pushOk} label="push" age={shortTimeAgo(card.syncStatus.lastPush)} warn={pushAgeH !== null && pushAgeH >= 24 && pushAgeH < 72} />
          </div>
        </div>
      </div>

      {/* Hover arrow */}
      <div className="absolute right-5 top-5 opacity-0 transition-opacity group-hover:opacity-100">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-white">
          <MoveRight className="h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  )
}
